<script lang="ts" setup>
import type { AccountRole, Address, RouterOutput } from '~/types'
import { format, subYears } from 'date-fns'
import { cityRoles } from '~/authorization'
import { personGenderSchema, personIsPregnantSchema, personIsSmokerSchema } from '~/server/schemas'

const props = withDefaults(defineProps<{
  predispositions?: RouterOutput['predispositions']['findManyPredisposition']['data']
  showName?: boolean
  personFields?: PersonFields
  selectedRole?: AccountRole
  hasReportingAddress?: boolean
}>(), {
  showName: true,
  personFields: true,
  hasReportingAddress: false,
})

type PersonInputs = keyof RouterOutput['persons']['findOnePerson']
export type PersonFields = Partial<Record<PersonInputs, boolean>> | boolean

function showInputField(key: PersonInputs) {
  if (typeof props.personFields === 'boolean') {
    return props.personFields
  }
  return !!props.personFields[key]
}

const genderOptions = generateTranslatedSelectOptions('person.gender', personGenderSchema.options)
const pregnantOptions = generateTranslatedSelectOptions('person.isPregnant', personIsPregnantSchema.options)
const predispositionOptions = computed(() => generateSelectOptions(props.predispositions))
const smokingOptions = generateTranslatedSelectOptions('person.isSmoker', personIsSmokerSchema.options)

const today = format(new Date(), 'yyyy-MM-dd')
const dateBefore18Years = format(subYears(new Date(), 18), 'yyyy-MM-dd')
const birthDateValidation = computed(() => {
  if (props.selectedRole && cityRoles.includes(props.selectedRole)) {
    return [['required'], ['date_before', dateBefore18Years]]
  } else {
    return [['required'], ['date_between', '1907-01-01', today]]
  }
})
const { isCityAccount } = useAuthorization()
const reportingAddress = ref<Address | undefined>(undefined)
const hasReportingAddress = ref(props.hasReportingAddress)
</script>

<template>
  <div>
    <div v-if="showName" class="grid md:grid-cols-2 md:gap-4">
      <FormKit
        name="firstName"
        type="text"
        :label="$t('account.firstName.label')"
        :placeholder="$t('account.firstName.placeholder')"
        validation="required"
      />
      <FormKit
        name="lastName"
        type="text"
        :label="$t('account.lastName.label')"
        :placeholder="$t('account.lastName.placeholder')"
        validation="required"
      />
    </div>
    <div class="md:grid grid-flow-col grid-rows-1 md:gap-4">
      <FormKitDate
        v-if="showInputField('birthDate')"
        name="birthDate"
        :label="$t('person.birthDate.label')"
        :validation="birthDateValidation"
      />
    </div>
    <div class="md:grid grid-flow-col grid-rows-1 md:gap-4">
      <FormKitAddress
        v-if="showInputField('address')"
        name="address"
        :label="$t('person.address.label')"
        :placeholder="$t('person.address.placeholder')"
        :validation="[['required'], ['address']]"
        :validation-messages="{ address: $t('address.invalid') }"
      />
      <FormKitToggle
        v-if="showInputField('reportingAddress')"
        v-model="hasReportingAddress"
        ignore
        :label="$t('person.reportingAddress.checkbox.label')"
      />
    </div>
    <div v-if="hasReportingAddress && showInputField('reportingAddress')" class="md:grid grid-flow-col grid-rows-1 md:gap-4">
      <FormKitAddress
        v-model="reportingAddress"
        name="reportingAddress"
        :label="$t('person.reportingAddress.label')"
        :placeholder="$t('person.address.placeholder')"
        :validation="[['required'], ['address']]"
        :validation-messages="{ address: $t('address.invalid') }"
      />
    </div>
    <template v-if="personFields === true">
      <div class="grid md:grid-cols-2 md:gap-4">
        <FormKitMultiSelect
          name="gender"
          :label="$t('person.gender.label')"
          :is-multi-select-allowed="false"
          :multi-select-options="genderOptions"
        />
        <FormKitMultiSelect
          name="isPregnant"
          :label="$t('person.isPregnant.label')"
          :is-multi-select-allowed="false"
          :multi-select-options="pregnantOptions"
        />
      </div>
      <div class="grid md:grid-cols-2 md:gap-4">
        <FormKit
          name="weightInKg"
          type="number"
          number
          :label="$t('person.weight.label')"
          :placeholder="$t('person.weight.placeholder')"
          :validation="[['min', 0]]"
          step="any"
        />
        <FormKit
          name="heightInCm"
          type="number"
          number
          :label="$t('person.height.label')"
          :placeholder="$t('person.height.placeholder')"
          :validation="[['min', 20.0]]"
          step="any"
        />
      </div>
      <div class="grid md:grid-cols-2 md:gap-4">
        <FormKitMultiSelect
          name="isSmoker"
          :label="$t('person.isSmoker.label')"
          :is-multi-select-allowed="false"
          :multi-select-options="smokingOptions"
        />
        <FormKitMultiSelect
          v-if="predispositionOptions"
          name="relevantPredispositions"
          :label="$t('person.relevantPredispositions.label')"
          :placeholder="$t('person.relevantPredispositions.placeholder')"
          :is-multi-select-allowed="true"
          :is-option-creation-allowed="false"
          :multi-select-options="predispositionOptions"
        />
      </div>
      <FormKit
        name="livingSituation"
        type="textarea"
        :label="$t('person.livingSituation.label')"
        :placeholder="$t('person.livingSituation.placeholder')"
      />
      <FormKitDate
        v-if="isCityAccount"
        name="deathDate"
        :label="$t('person.deathDate.label')"
        :validation="[['date_before', today]]"
        :validation-messages="{ date_before: $t('date.todayOrBefore.error') }"
      />
    </template>
  </div>
</template>
